import React, { useState, useEffect } from 'react';
import { fetchData, fetchDairyDetails } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import Button from '@mui/material/Button';
import { TableContainer, Table, TableBody, TableRow, TableCell, Paper, TableHead } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CustomCalendar from './Calendar';
import axios from 'axios';

// Import custom CSS
import './custom-datepicker.css';

function StaffDairy() {
    // const history = useHistory();
    const [daysOfMonth, setDaysOfMonth] = useState([]);
    const [staffOptions, setStaffOptions] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [staffId, setStaffId] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const { isAuthenticated, companyId, token, userId, logout } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        const date = new Date();
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        setFromDate(firstDay);
        setToDate(lastDay);
    }, []);

    useEffect(() => {
        fetchStaff();
    }, []);

    const fetchStaff = async () => {
        try {
            const response = await fetchData('StaffApi', `/${companyId}`);
            if (response.responseCode === '1111') {
                const options = response.response_data.map(staff => ({
                    value: staff.id,
                    label: staff.firstName,
                }));
                setStaffOptions(options);
            }
        } catch (error) {
            console.error('Error fetching Staff:', error);
        }
    };

    const handleStaffChange = async (selectedOption) => {
        setSelectedStaff(selectedOption);
        const formattedFromDate = formatDate(fromDate);
        const formattedToDate = formatDate(toDate);
        setIsLoading(true);
        setStaffId(selectedOption.value);
        try {
            const response = await fetchDairyDetails('EngineerDairyAPI', formattedFromDate, formattedToDate, selectedOption.value, companyId, token);
            if (response.responseCode === '1111' && response.response_data) {
                setDaysOfMonth(response.response_data);
            } else {
                console.error('Invalid response format', response);
            }
        } catch (error) {
            console.error('Error fetching days of the week:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const year = d.getFullYear();
        return `${year}-${month}-${day}`;
    };

    const dayColors = {
        Sunday: 'red',
        Monday: 'darkgreen',
        Tuesday: 'darkgreen',
        Wednesday: 'darkgreen',
        Thursday: 'darkgreen',
        Friday: 'darkgreen',
        Saturday: 'darkred'
    };

    const getDayName = (dateString) => {
        const date = new Date(dateString);
        const options = { weekday: 'long' };
        return date.toLocaleDateString('en-US', options);
    };

    const CustomTableBody = ({ daysOfMonth, setDaysOfMonth }) => {
        const handleCheckboxChange = (id) => {
            setDaysOfMonth(prevDaysOfMonth =>
                prevDaysOfMonth.map(day =>
                    day.ID === id ? { ...day, IsAvailable: !day.IsAvailable } : day
                )
            );
        };

        const handleUpdate = async (event) => {
            event.preventDefault();
            setIsLoading(true);

            const updatedScheduleData = {
                staffAppointments: daysOfMonth.map(item => ({
                    ...item,
                    UpdatedBy: userId,
                })),
            };

            try {
                const url = `EngineerDairyAPI/${staffId}`;
                // const baseURL = 'https://localhost:7291/api/';
                const baseURL = 'https://pp-api.off-ease.com/api/';
                const response = await axios.put(
                    `${baseURL}${url}`,
                    updatedScheduleData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                setSuccessMessage("WeeklySchedule Updated successfully!");
                setErrorMessage("");
                setTimeout(() => {
                    setSuccessMessage("");
                    window.location.reload();
                }, 3000);
            } catch (error) {
                console.error('Schedule Update error:', error);
                if (error.response) {
                    console.error('Error response data:', error.response.data);
                    console.error('Error response status:', error.response.status);
                    console.error('Error response headers:', error.response.headers);
                }
                setErrorMessage("Failed to update the schedule. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        return (
            <div className="w-full max-w-sm container mt-2 mx-auto" style={{ maxWidth: '100%' }}>
                <div className="w-full mb-2 flex">
                    <h6 className='productHead flex-grow linkcolor' style={{ color: 'black', fontFamily: 'Georgia, serif', fontWeight: 'bolder', marginLeft: '25px' }}>ENGINEER DIARY</h6>
                </div>
                <div className="flex" style={{ maxHeight: '600px' }}>
                    <div className="w-1/2 pr-4" style={{ width: '45%', marginLeft: '25px' }}>
                        <div className="bg-blue-450 rounded-lg shadow-lg p-6">
                            <div className="w-full mb-4 flex">
                                <div className="w-1/4 mr-2">
                                    From Date
                                    <DatePicker
                                        selected={fromDate}
                                        onChange={(date) => setFromDate(date)}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="From Date"
                                    />
                                </div>
                                <div className="w-1/4 mr-2">
                                    To Date
                                    <DatePicker
                                        selected={toDate}
                                        onChange={(date) => setToDate(date)}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="To Date"
                                    />
                                </div>
                                <div className="w-1/2 mr-2">
                                    Engineer
                                    <Select
                                        value={selectedStaff}
                                        onChange={handleStaffChange}
                                        options={staffOptions}
                                        placeholder="Select Staff"
                                        isSearchable
                                        styles={{ width: '350px' }}
                                    />
                                </div>
                            </div>

                            {/* <div className="w-full mb-4 flex" style={{ marginTop: '-20px', height: '470px' }}>
                                <CustomCalendar daysOfMonth={daysOfMonth} />
                            </div> */}

                            <div className="w-full mb-4 flex" style={{ marginTop: '-20px', height: '370px' }}>
                                <CustomCalendar daysOfMonth={daysOfMonth} />
                            </div>
                        </div>
                    </div>

                    <div className="w-1/2 pl-4" style={{ width: '55%' }}>
                        <div className="bg-teal-450 rounded-lg shadow-lg p-6">
                            <div className="w-full mb-4 flex-horizontal">
                                {/* <TableContainer component={Paper} style={{ width: '750px', maxHeight: '550px', overflowY: 'auto' }}> */}
                                <TableContainer component={Paper} style={{ width: '650px', maxHeight: '450px', overflowY: 'auto' }}>
                                    <Table stickyHeader>
                                        <TableHead sx={{ backgroundColor: '#090040' }}>
                                            <TableRow>
                                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontWeight: 'bolder', fontSize: '12px', padding: '5px', width: '15%', color: 'white', textTransform: 'uppercase' }}>DATE</TableCell>
                                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontWeight: 'bolder', fontSize: '12px', padding: '5px', width: '15%', color: 'white', textTransform: 'uppercase' }}>DAY</TableCell>
                                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontWeight: 'bolder', fontSize: '12px', padding: '5px', width: '14%', color: 'white', textTransform: 'uppercase' }}>IS AVAILABLE</TableCell>
                                                {daysOfMonth.length > 0 && (
                                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontWeight: 'bolder', fontSize: '12px', padding: '5px', width: '14%' }}>
                                                        <Button onClick={handleUpdate} style={{ color: 'white', fontWeight: 'bolder', backgroundColor: 'darkred' }} disabled={isLoading}>
                                                            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Update"}
                                                        </Button>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {Array.isArray(daysOfMonth) && daysOfMonth.length > 0 ? (
                                                daysOfMonth.map((item, index) => (
                                                    <TableRow key={index} className="customTableRows" style={{ backgroundColor: 'white' }}>
                                                        <TableCell sx={{ fontSize: '13px', width: '14%', padding: '5px', fontWeight: 'bolder' }}>
                                                            {item.Date && new Date(item.Date).toLocaleDateString('en-GB')}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '13px', width: '14%', padding: '5px', fontWeight: 'bolder', color: dayColors[getDayName(item.Date)] }}>
                                                            {getDayName(item.Date)}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '13px', width: '14%', padding: '5px', fontWeight: 'bolder' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={item.IsAvailable}
                                                                onChange={() => handleCheckboxChange(item.ID)}
                                                                style={{ cursor: 'pointer', height: '20px' }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '13px', width: '14%', padding: '5px', fontWeight: 'bolder' }}></TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} style={{ textAlign: 'center' }}>No data available</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <CustomTableBody daysOfMonth={daysOfMonth} setDaysOfMonth={setDaysOfMonth} />
    );
}

export default StaffDairy;
